import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  Label,
  CheckBox,
  Toast,
  TabContainer,
  Tab,
  FlexBox
} from '@ui5/webcomponents-react';
import { useStyles } from '../ManageQueries.jss';
import { useTranslation } from 'react-i18next';
import { SearchColumnsVisibility } from 'stores/interfaces/SearchColumnsState';
import { env } from 'config/env';
import { getColumnConfig } from '../Filters/StandardView';
import { getSearchColumnVisibility } from 'utils/searchColumnVisibility';

import TabContainerBackgroundDesign from '@ui5/webcomponents/dist/types/BackgroundDesign.js';

const columnToI18nMap = {
  id: 'id',
  deadline: 'deadline',
  status: 'status',
  translation: 'translation',
  source: 'source',
  issue: 'issue',
  language: 'language',
  creator: 'creator',
  queryCreatedAt: 'createdAt',
  assignee: 'assignee',
  projectName: 'project-name',
  projectManager: 'project-manager',
  qualityExpert: 'quality-engineer',
  qmContact: 'qm-contact',
  xtmProjectName: 'xtm-project-name',
  filename: 'file-name',
  submitterGroupName: 'submitter-group-name',
  loio: 'loio',
  repository: 'repository',
  collection: 'collection',
  stringId: 'string-id',
  queryTrackerId: 'query-tracker-id',
  account: 'account',
  applicationComponent: 'application-component',
  substitutePm: 'substitute-pm',
  sourceContact: 'source-contact'
};

type KeySetSearchColumnsVisibility = keyof SearchColumnsVisibility;
const orderedColumnKeys: KeySetSearchColumnsVisibility[] = [
  // question-information
  'id',
  'deadline',
  'status',
  'translation',
  'source',
  'issue',
  'language',
  'projectName',
  // administrative-details
  'queryTrackerId',
  'creator',
  'queryCreatedAt',
  'submitterGroupName',
  'assignee',
  'projectManager',
  'qualityExpert',
  'qmContact',
  'substitutePm',
  'account',
  // technical-information
  'xtmProjectName',
  'filename',
  'loio',
  'repository',
  'collection',
  'stringId',
  'applicationComponent',
  'sourceContact'
];

const internalUserOnlyColumnKeys: KeySetSearchColumnsVisibility[] = [
  'assignee',
  'projectManager',
  'qualityExpert',
  'qmContact',
  'substitutePm',
  'account',
  'applicationComponent',
  'sourceContact'
];

type SearchColumnsDialogProps = {
  open: boolean;
  isInternalUser: boolean;
  setSearchColumnsDialogOpen: (isOpen: boolean) => void;
  searchColumnsVisibility: SearchColumnsVisibility;
  setSearchColumnsVisibility: (searchColumnsVisibility: SearchColumnsVisibility) => void;
};

export const SearchColumnsDialog: React.FC<SearchColumnsDialogProps> = (props) => {
  const classes = useStyles();
  const {
    open,
    isInternalUser,
    setSearchColumnsDialogOpen,
    searchColumnsVisibility,
    setSearchColumnsVisibility
  } = props;
  const { t } = useTranslation();
  const toastRef = useRef<any>();
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const TOAST_DURATION = 3000;
  const allowQtImports = env?.REACT_APP_ALLOW_QUERY_TRACKER_IMPORTS === 'true' ? true : false;

  const [searchColumnsDialogVisibility, setSearchColumnsDialogVisibility] =
    useState(searchColumnsVisibility);

  const onReset = () => {
    const columnConfig = getColumnConfig(isInternalUser);
    setSearchColumnsDialogVisibility(getSearchColumnVisibility(columnConfig));
  };

  const onChangeColumnCheckbox = (item: any, event: any) => {
    setSearchColumnsDialogVisibility((prev: any) => ({ ...prev, [item]: event.target.checked }));
  };

  useEffect(() => {
    setSearchColumnsDialogVisibility(searchColumnsVisibility);
  }, [searchColumnsVisibility]);

  const renderCheckboxes = (start: number, end: number) => {
    const renderedCheckboxes: JSX.Element[] = [];
    orderedColumnKeys.slice(start, end).forEach((k) => {
      const key = k as keyof SearchColumnsVisibility;
      renderedCheckboxes.push(
        <div key={key}>
          <CheckBox
            disabled={
              searchColumnsDialogVisibility[key] || isInternalUser
                ? false
                : internalUserOnlyColumnKeys.includes(key)
            }
            key={key}
            id={`manage-queries-list-table-column-${key}`}
            text={t(`manage-queries-list-table-column-${columnToI18nMap[key]}`)}
            data-testid={`manage-queries-list-customize-checkbox-${key}`}
            checked={searchColumnsDialogVisibility[key]}
            onChange={(event) => {
              onChangeColumnCheckbox(key, event);
            }}
            wrappingType="None"
          />
        </div>
      );
    });

    return renderedCheckboxes;
  };

  const onClickOk = () => {
    const hasSelectedAny = Object.values(searchColumnsDialogVisibility).lastIndexOf(true);
    if (hasSelectedAny === -1) {
      setToastMessage(t('manage-queries-list-column-dialog-none-selected'));
      setToastOpen(true);
    } else {
      setSearchColumnsVisibility(searchColumnsDialogVisibility);
      setSearchColumnsDialogOpen(false);
    }
  };

  const onClickClose = () => {
    setSearchColumnsDialogVisibility(searchColumnsVisibility);
    setSearchColumnsDialogOpen(false);
  };

  useEffect(() => {
    const onEscKeyPressed = (event: any) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSearchColumnsDialogOpen(false);
      }
    };

    document.addEventListener('keydown', onEscKeyPressed);

    return () => {
      document.removeEventListener('keydown', onEscKeyPressed);
    };
  }, []);

  const getAdministrativeColumns = () => {
    if (isInternalUser) {
      if (allowQtImports) {
        return renderCheckboxes(8, 18);
      } else {
        return renderCheckboxes(9, 18);
      }
    } else {
      if (allowQtImports) {
        return renderCheckboxes(8, 11);
      } else {
        return renderCheckboxes(9, 11);
      }
    }
  };

  const getTechnicalInformationColumns = () => {
    if (isInternalUser) {
      return renderCheckboxes(18, 26);
    }
    return renderCheckboxes(18, 24);
  };

  return (
    <Dialog
      id="search-columns-dialog"
      open={open}
      data-testid="manage-queries-columns-dialog"
      data-help-id="manage-queries-columns-dialog"
      header={
        <>
          <Toast
            ref={toastRef}
            open={toastOpen}
            onClose={() => setToastOpen(false)}
            duration={TOAST_DURATION}
          >
            {toastMessage}
          </Toast>
          <FlexBox justifyContent="SpaceBetween" alignItems="Baseline" fitContainer={true}>
            <Label wrappingType="None">{t('manage-queries-list-column-dialog-title')}</Label>
            <Button
              design="Transparent"
              data-testid="manage-queries-list-column-dialog-button-reset"
              data-help-id="manage-queries-list-column-dialog-button-reset"
              className={classes.actionGroupItem}
              onClick={() => {
                onReset();
              }}
            >
              {t('manage-queries-list-column-dialog-button-reset')}
            </Button>
          </FlexBox>
        </>
      }
      footer={
        <>
          <Button
            className={classes.actionGroupItem}
            data-testid="manage-queries-list-column-dialog-button-ok"
            data-help-id="manage-queries-list-column-dialog-button-ok"
            design="Emphasized"
            onClick={() => {
              onClickOk();
            }}
          >
            {t('manage-queries-list-column-dialog-button-ok')}
          </Button>

          <Button
            className={classes.actionGroupItem}
            data-testid="manage-queries-list-column-dialog-button-close"
            data-help-id="manage-queries-list-column-dialog-button-close"
            onClick={() => {
              onClickClose();
            }}
          >
            {t('manage-queries-list-column-dialog-button-close')}
          </Button>
        </>
      }
    >
      {open && (
        <TabContainer contentBackgroundDesign={TabContainerBackgroundDesign.Transparent}>
          <Tab text={t('manage-queries-list-column-dialog-tab-question-information')} selected>
            {renderCheckboxes(0, 8)}
          </Tab>
          <Tab text={t('manage-queries-list-column-dialog-tab-technical-information')}>
            {getTechnicalInformationColumns()}
          </Tab>
          <Tab text={t('manage-queries-list-column-dialog-tab-administrative-details')}>
            {getAdministrativeColumns()}
          </Tab>
        </TabContainer>
      )}
    </Dialog>
  );
};
